import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatUTCDateTimeToLocal, calculateDuration, getCurrentLocalTime } from '../../utils/timezone';
import './CheckInPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface CheckInStatus {
  is_checked_in: boolean;
  check_in_time?: string;
  check_out_time?: string;
  task_id?: number;
  task_name?: string;
  work_location?: string;
  previous_task_id?: number;
  previous_work_location?: string;
}

const normalizeWorkLocation = (location?: string): string => {
  const normalized = (location || '').toLowerCase().trim();
  if (normalized === 'on-site' || normalized === 'onsite' || normalized === 'on_site') {
    return 'on-site';
  }
  return 'remote';
};

interface Task {
  id: number;
  name: string;
  category: string;
  description: string;
  isActive: boolean;
}

type ClockTime = {
  hour: number;
  minute: number;
  period: 'AM' | 'PM';
};

const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES = [0, 15, 30, 45];
const PERIODS: Array<'AM' | 'PM'> = ['AM', 'PM'];
const WHEEL_ITEM_H = 44;

const pad2 = (n: number) => String(n).padStart(2, '0');

const formatClock = (t: ClockTime) => `${t.hour}:${pad2(t.minute)} ${t.period}`;

const toDateInputValue = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const dateToClock = (d: Date): ClockTime => {
  const h24 = d.getHours();
  const minute = Math.floor(d.getMinutes() / 15) * 15;
  const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
  let hour = h24 % 12;
  if (hour === 0) hour = 12;
  return { hour, minute, period };
};

const combineLocal = (dateStr: string, t: ClockTime): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  let hour = t.hour % 12;
  if (t.period === 'PM') hour += 12;
  return new Date(year, month - 1, day, hour, t.minute, 0, 0);
};

const roundDown15 = (d: Date): Date => {
  const out = new Date(d);
  out.setSeconds(0, 0);
  out.setMinutes(Math.floor(out.getMinutes() / 15) * 15);
  return out;
};

const defaultPastTimes = () => {
  const checkOut = roundDown15(new Date());
  let checkIn = roundDown15(new Date(checkOut.getTime() - 60 * 60 * 1000));
  if (toDateInputValue(checkIn) !== toDateInputValue(checkOut)) {
    checkIn = new Date(checkOut);
    checkIn.setHours(0, 0, 0, 0);
  }
  if (checkIn.getTime() >= checkOut.getTime()) {
    checkIn = new Date(checkOut.getTime() - 15 * 60 * 1000);
  }
  return {
    date: toDateInputValue(checkOut),
    inClock: dateToClock(checkIn),
    outClock: dateToClock(checkOut),
  };
};

const minPastDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return toDateInputValue(d);
};

function WheelColumn<T extends string | number>({
  items,
  value,
  onChange,
  format,
}: {
  items: readonly T[];
  value: T;
  onChange: (v: T) => void;
  format?: (v: T) => string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const skipScroll = useRef(true);

  useLayoutEffect(() => {
    const idx = items.indexOf(value);
    if (ref.current && idx >= 0) {
      ref.current.scrollTop = idx * WHEEL_ITEM_H;
    }
    const timer = window.setTimeout(() => {
      skipScroll.current = false;
    }, 80);
    return () => window.clearTimeout(timer);
    // Only snap to the opening value once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = () => {
    if (!ref.current || skipScroll.current) return;
    const idx = Math.round(ref.current.scrollTop / WHEEL_ITEM_H);
    const clamped = Math.max(0, Math.min(items.length - 1, idx));
    if (items[clamped] !== value) {
      onChange(items[clamped]);
    }
  };

  return (
    <div className="time-wheel-col" ref={ref} onScroll={handleScroll}>
      <div className="time-wheel-spacer" aria-hidden="true" />
      {items.map((item) => (
        <div
          key={String(item)}
          className={`time-wheel-item${item === value ? ' selected' : ''}`}
          onClick={() => {
            onChange(item);
            const idx = items.indexOf(item);
            ref.current?.scrollTo({ top: idx * WHEEL_ITEM_H, behavior: 'smooth' });
          }}
        >
          {format ? format(item) : String(item)}
        </div>
      ))}
      <div className="time-wheel-spacer" aria-hidden="true" />
    </div>
  );
}

const TimeWheelSheet: React.FC<{
  title: string;
  value: ClockTime;
  onCancel: () => void;
  onDone: (next: ClockTime) => void;
}> = ({ title, value, onCancel, onDone }) => {
  const [draft, setDraft] = useState<ClockTime>(value);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div className="time-sheet-overlay" onClick={onCancel} role="presentation">
      <div
        className="time-sheet"
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="time-sheet-handle" />
        <div className="time-sheet-header">
          <button type="button" className="time-sheet-cancel" onClick={onCancel}>
            Cancel
          </button>
          <span className="time-sheet-title">{title}</span>
          <button type="button" className="time-sheet-done" onClick={() => onDone(draft)}>
            Done
          </button>
        </div>
        <div className="time-sheet-wheels">
          <div className="time-sheet-highlight" aria-hidden="true" />
          <WheelColumn
            items={HOURS}
            value={draft.hour}
            onChange={(hour) => setDraft((current) => ({ ...current, hour }))}
          />
          <WheelColumn
            items={MINUTES}
            value={draft.minute}
            onChange={(minute) => setDraft((current) => ({ ...current, minute }))}
            format={pad2}
          />
          <WheelColumn
            items={PERIODS}
            value={draft.period}
            onChange={(period) => setDraft((current) => ({ ...current, period }))}
          />
        </div>
      </div>
    </div>
  );
};

const CheckInPage: React.FC = () => {
  const [checkInStatus, setCheckInStatus] = useState<CheckInStatus>({ is_checked_in: false });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<number>(1); // Default until preferences load
  const [workLocation, setWorkLocation] = useState<string>('remote'); // Default until preferences load
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [justCheckedIn, setJustCheckedIn] = useState(false);
  const [justCheckedOut, setJustCheckedOut] = useState(false);
  const [justLoggedPast, setJustLoggedPast] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [pastTimeOpen, setPastTimeOpen] = useState(false);
  const [pastDate, setPastDate] = useState(() => defaultPastTimes().date);
  const [inClock, setInClock] = useState<ClockTime>(() => defaultPastTimes().inClock);
  const [outClock, setOutClock] = useState<ClockTime>(() => defaultPastTimes().outClock);
  const [timeSheet, setTimeSheet] = useState<null | 'in' | 'out'>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get passcode from location state or localStorage
  const passcode = location.state?.passcode || localStorage.getItem('currentPasscode') || '';

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tasks`);
      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks || []);
      }
    } catch (err) {
      // Tasks fetch failed - use empty array
      setTasks([]);
    }
  }, []);

  const checkCurrentStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/checkin-status/${passcode}`);
      if (response.ok) {
        const data: CheckInStatus = await response.json();
        setCheckInStatus(data);
      }
    } catch (err) {
      // Status check failed silently
    } finally {
      setStatusLoaded(true);
    }
  }, [passcode]);

  // Load status + tasks whenever passcode changes
  useEffect(() => {
    if (passcode) {
      setStatusLoaded(false);
      setDefaultsApplied(false);
      setSelectedTaskId(1);
      setWorkLocation('remote');
      checkCurrentStatus();
      fetchTasks();
    } else {
      setError('No user code found. Please login again.');
    }
  }, [passcode, checkCurrentStatus, fetchTasks]);

  // Apply previous (or current) task/location once status + tasks are both ready.
  useEffect(() => {
    if (!statusLoaded || !tasks.length || defaultsApplied) return;

    if (checkInStatus.is_checked_in) {
      if (checkInStatus.task_id && tasks.some((t) => t.id === checkInStatus.task_id)) {
        setSelectedTaskId(checkInStatus.task_id);
      }
      if (checkInStatus.work_location) {
        setWorkLocation(normalizeWorkLocation(checkInStatus.work_location));
      }
    } else {
      if (
        checkInStatus.previous_task_id &&
        tasks.some((t) => t.id === checkInStatus.previous_task_id)
      ) {
        setSelectedTaskId(checkInStatus.previous_task_id);
      }
      if (checkInStatus.previous_work_location) {
        setWorkLocation(normalizeWorkLocation(checkInStatus.previous_work_location));
      }
    }

    setDefaultsApplied(true);
  }, [statusLoaded, tasks, checkInStatus, defaultsApplied]);

  const handleCheckIn = async () => {
    setIsLoading(true);
    setError('');

    const payload: {
      passcode: string;
      task_id: number;
      work_location: string;
      check_in_time?: string;
      check_out_time?: string;
    } = { passcode, task_id: selectedTaskId, work_location: workLocation };

    if (pastTimeOpen) {
      const checkInAt = combineLocal(pastDate, inClock);
      const checkOutAt = combineLocal(pastDate, outClock);
      const now = new Date();

      if (checkOutAt.getTime() <= checkInAt.getTime()) {
        setError('Check-out must be after check-in.');
        setIsLoading(false);
        return;
      }
      if (checkInAt.getTime() > now.getTime() || checkOutAt.getTime() > now.getTime()) {
        setError('You can only log times that have already happened.');
        setIsLoading(false);
        return;
      }

      payload.check_in_time = checkInAt.toISOString();
      payload.check_out_time = checkOutAt.toISOString();
    }

    try {
      const response = await fetch(`${API_BASE_URL}/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        await checkCurrentStatus();
        setCountdown(5);
        if (pastTimeOpen) {
          setJustLoggedPast(true);
        } else {
          setJustCheckedIn(true);
        }
      } else {
        setError(data.message || 'Failed to check in');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const finishedAction = justCheckedIn || justCheckedOut || justLoggedPast;

  // Auto-logout countdown after check-in, check-out, or past time log
  useEffect(() => {
    if (finishedAction && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (finishedAction && countdown === 0) {
      localStorage.removeItem('currentPasscode');
      localStorage.removeItem('canAddDependents');
      navigate('/');
    }
  }, [finishedAction, countdown, navigate]);

  const handleCheckOut = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: passcode }),
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh the check-in status to get the latest data from the server
        await checkCurrentStatus();
        // Start the auto-logout countdown
        setJustCheckedOut(true);
        setCountdown(5);
      } else {
        setError(data.message || 'Failed to check out');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/success');
  };

  const formatTime = (timeString: string) => {
    return formatUTCDateTimeToLocal(timeString);
  };

  const formatDuration = (checkInTime: string) => {
    return calculateDuration(checkInTime);
  };

  return (
    <div className="checkin-container">
      <div className="checkin-card">
        <div className="checkin-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="checkin-title">Check In/Out</h1>
          <div className="current-time">
            {getCurrentLocalTime()}
          </div>
        </div>

        <div className="checkin-content">
          {error && <div className="error-message">{error}</div>}
          
          <div className="status-section">
            <div className={`status-indicator ${checkInStatus.is_checked_in ? 'checked-in' : 'checked-out'}`}>
              <div className="status-dot"></div>
              <span className="status-text">
                {checkInStatus.is_checked_in ? 'Currently Checked In' : 'Currently Checked Out'}
              </span>
            </div>

            {checkInStatus.is_checked_in && checkInStatus.check_in_time && (
              <div className="time-info">
                <div className="time-item">
                  <span className="time-label">Check In Time:</span>
                  <span className="time-value">{formatTime(checkInStatus.check_in_time)}</span>
                </div>
                <div className="time-item">
                  <span className="time-label">Duration:</span>
                  <span className="time-value">{formatDuration(checkInStatus.check_in_time)}</span>
                </div>
                {checkInStatus.check_out_time && (
                  <div className="time-item">
                    <span className="time-label">Check Out Time:</span>
                    <span className="time-value">{formatTime(checkInStatus.check_out_time)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {!checkInStatus.is_checked_in && !justLoggedPast && (
            <>
              <div className="work-location-section">
                <label className="task-label">Work Location:</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="workLocation"
                      value="remote"
                      checked={workLocation === 'remote'}
                      onChange={(e) => setWorkLocation(e.target.value)}
                    />
                    <span className="radio-text">Remote</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="workLocation"
                      value="on-site"
                      checked={workLocation === 'on-site'}
                      onChange={(e) => setWorkLocation(e.target.value)}
                    />
                    <span className="radio-text">On-Site</span>
                  </label>
                </div>
              </div>

              <div className="task-selection-section">
                <label htmlFor="task-select" className="task-label">
                  Select Task:
                </label>
                <select
                  id="task-select"
                  value={selectedTaskId}
                  onChange={(e) => setSelectedTaskId(Number(e.target.value))}
                  className="task-select"
                >
                  {Array.isArray(tasks) && tasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={`past-time-section${pastTimeOpen ? ' open' : ''}`}>
                <button
                  type="button"
                  className="past-time-toggle"
                  onClick={() => {
                    if (!pastTimeOpen) {
                      const defaults = defaultPastTimes();
                      setPastDate(defaults.date);
                      setInClock(defaults.inClock);
                      setOutClock(defaults.outClock);
                    }
                    setPastTimeOpen((open) => !open);
                    setError('');
                  }}
                  aria-expanded={pastTimeOpen}
                >
                  <span className="past-time-toggle-label">
                    {pastTimeOpen ? 'Click to go back to normal check-in' : 'Forgot to check in?'}
                  </span>
                  <span className="past-time-chevron" aria-hidden="true">
                    {pastTimeOpen ? '▴' : '▾'}
                  </span>
                </button>

                {pastTimeOpen && (
                  <div className="past-time-fields">
                    <label className="task-label" htmlFor="past-date">
                      Date
                    </label>
                    <input
                      id="past-date"
                      type="date"
                      className="task-select past-date-input"
                      value={pastDate}
                      min={minPastDate()}
                      max={toDateInputValue(new Date())}
                      onChange={(e) => {
                        const next = e.target.value;
                        setPastDate(next);
                        const now = new Date();
                        if (combineLocal(next, inClock) > now) {
                          setInClock(dateToClock(roundDown15(now)));
                        }
                        if (combineLocal(next, outClock) > now) {
                          setOutClock(dateToClock(roundDown15(now)));
                        }
                      }}
                    />

                    <label className="task-label" htmlFor="past-check-in">
                      Check in
                    </label>
                    <button
                      id="past-check-in"
                      type="button"
                      className="task-select past-time-field"
                      onClick={() => setTimeSheet('in')}
                    >
                      {formatClock(inClock)}
                    </button>

                    <label className="task-label" htmlFor="past-check-out">
                      Check out
                    </label>
                    <button
                      id="past-check-out"
                      type="button"
                      className="task-select past-time-field"
                      onClick={() => setTimeSheet('out')}
                    >
                      {formatClock(outClock)}
                    </button>

                    {combineLocal(pastDate, outClock).getTime() >
                      combineLocal(pastDate, inClock).getTime() && (
                      <p className="past-time-duration">
                        {(
                          (combineLocal(pastDate, outClock).getTime() -
                            combineLocal(pastDate, inClock).getTime()) /
                          3600000
                        ).toFixed(1)}{' '}
                        hours
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {checkInStatus.is_checked_in && checkInStatus.task_name && (
            <div className="current-task-section">
              {checkInStatus.work_location && (
                <div className="current-task">
                  <span className="task-label">Work Location:</span>
                  <span className="task-name">{checkInStatus.work_location === 'on-site' ? 'On-Site' : 'Remote'}</span>
                </div>
              )}
              <div className="current-task">
                <span className="task-label">Current Task:</span>
                <span className="task-name">{checkInStatus.task_name}</span>
              </div>
            </div>
          )}

          <div className="action-section">
            {justLoggedPast ? (
              <div className="auto-logout-message past-logged">
                <p>✓ Past time logged!</p>
                <p className="countdown-text">Logging out in {countdown} seconds...</p>
              </div>
            ) : justCheckedIn ? (
              <div className="auto-logout-message">
                <p>✓ Successfully checked in!</p>
                <p className="countdown-text">Logging out in {countdown} seconds...</p>
              </div>
            ) : justCheckedOut ? (
              <div className="auto-logout-message">
                <p>✓ Successfully checked out!</p>
                <p className="countdown-text">Logging out in {countdown} seconds...</p>
              </div>
            ) : (
              <button
                onClick={checkInStatus.is_checked_in ? handleCheckOut : handleCheckIn}
                className={`action-button ${
                  checkInStatus.is_checked_in ? 'checkout-button' : 'checkin-button'
                } ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="loading-spinner"></div>
                ) : checkInStatus.is_checked_in ? (
                  'Check Out'
                ) : pastTimeOpen ? (
                  'Log Past Time'
                ) : (
                  'Check In'
                )}
              </button>
            )}
          </div>
        </div>

        <div className="checkin-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Menu
          </button>
        </div>
      </div>

      {timeSheet && (
        <TimeWheelSheet
          title={timeSheet === 'in' ? 'Check-in time' : 'Check-out time'}
          value={timeSheet === 'in' ? inClock : outClock}
          onCancel={() => setTimeSheet(null)}
          onDone={(next) => {
            const combined = combineLocal(pastDate, next);
            if (combined.getTime() > Date.now()) {
              setError('You can only pick a time that has already happened.');
              setTimeSheet(null);
              return;
            }
            setError('');
            if (timeSheet === 'in') {
              setInClock(next);
            } else {
              setOutClock(next);
            }
            setTimeSheet(null);
          }}
        />
      )}
    </div>
  );
};

export default CheckInPage;
